import React, { useState, useEffect, useMemo } from 'react';
import { 
    Calculator, Save, Plus, Trash2, Edit2, ChevronDown, ChevronRight,
    Table, FunctionSquare, Copy, X, AlertTriangle, Check, Info, RefreshCw,
    Layers
} from 'lucide-react';
import { apiFetch } from '../utils/api';

/**
 * ScoringStrategyManager - SuperAdmin component for managing scoring strategies
 * 
 * Features:
 * - View/edit platform default strategy
 * - Create/edit/delete custom strategies for clubs
 * - Manage scoring ranges (table-based or formula-based)
 * - Preview scoring tables
 * - Formula editor with variable helpers
 */

// Helper to calculate score preview
const calculatePreview = (config, playerCount, rank) => {
    if (!config?.ranges) return 0;
    
    const range = config.ranges.find(r => {
        const minOk = playerCount >= r.min_players;
        const maxOk = r.max_players === null || playerCount <= r.max_players;
        return minOk && maxOk;
    });
    
    if (!range) return 0;
    
    if (range.strategy_type === 'table' && range.tableRules) {
        const rules = range.tableRules;
        // Convert array to object if needed
        const rulesObj = Array.isArray(rules) 
            ? rules.reduce((acc, r) => {
                if (!acc[r.player_count]) acc[r.player_count] = {};
                acc[r.player_count][r.rank] = r.points;
                return acc;
              }, {})
            : rules;
        return rulesObj[playerCount]?.[rank] ?? 0;
    }
    
    if (range.strategy_type === 'formula' && range.formula) {
        const vars = range.formula.variables || {};
        const multiplier = vars.multiplier || 2;
        const pot = playerCount * multiplier;
        
        // Simple evaluation for preview (full eval on server)
        if (rank === 1) return Math.floor(pot * (vars.rank1Percent || 0.5));
        if (rank === 2) return Math.floor(pot * (vars.rank2Percent || 0.3));
        if (rank === 3) return Math.floor(pot * (vars.rank3Percent || 0.2));
        return 0;
    }
    
    return 0;
};

// Range Editor Component
const RangeEditor = ({ range, onUpdate, onDelete, strategyId, apiPrefix = '/api/superadmin/scoring', readOnly = false }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [localRange, setLocalRange] = useState(range);
    const [formula, setFormula] = useState(range.formula || { expression: '', variables: {} });
    const [tableRules, setTableRules] = useState({});
    const [formulaError, setFormulaError] = useState(null);
    
    useEffect(() => {
        setLocalRange(range);
        setFormula(range.formula || { expression: '', variables: {} });
        
        // Convert table rules array to object
        if (range.tableRules && Array.isArray(range.tableRules)) {
            const rulesObj = {};
            range.tableRules.forEach(r => {
                if (!rulesObj[r.player_count]) rulesObj[r.player_count] = {};
                rulesObj[r.player_count][r.rank] = r.points;
            });
            setTableRules(rulesObj);
        } else {
            setTableRules(range.tableRules || {});
        }
    }, [range]);
    
    const saveTableRules = async () => {
        if (readOnly) return;
        setSaving(true);
        try {
            // Use club admin endpoint if not superadmin prefix
            const endpoint = apiPrefix.includes('superadmin') 
                ? `/api/superadmin/scoring/ranges/${range.id}/table-rules`
                : `/api/scoring/ranges/${range.id}/table-rules`;
            const res = await apiFetch(endpoint, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rules: tableRules })
            });
            if (res.ok) {
                setEditMode(false);
                onUpdate();
            }
        } catch (err) {
            console.error('Failed to save table rules:', err);
        } finally {
            setSaving(false);
        }
    };
    
    const saveFormula = async () => {
        if (readOnly) return;
        setSaving(true);
        setFormulaError(null);
        try {
            const endpoint = apiPrefix.includes('superadmin')
                ? `/api/superadmin/scoring/ranges/${range.id}/formula`
                : `/api/scoring/ranges/${range.id}/formula`;
            const res = await apiFetch(endpoint, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    expression: formula.expression,
                    variables: formula.variables
                })
            });
            if (res.ok) {
                setEditMode(false);
                setFormulaError(null);
                onUpdate();
            } else {
                const data = await res.json();
                setFormulaError({
                    message: data.error || 'Tallennus epäonnistui',
                    suggestion: data.suggestion
                });
            }
        } catch (err) {
            console.error('Failed to save formula:', err);
            setFormulaError({ message: 'Verkkovirhe' });
        } finally {
            setSaving(false);
        }
    };
    
    const updateTableValue = (playerCount, rank, value) => {
        setTableRules(prev => ({
            ...prev,
            [playerCount]: {
                ...prev[playerCount],
                [rank]: parseInt(value) || 0
            }
        }));
    };
    
    const updateFormulaVariable = (key, value) => {
        setFormula(prev => ({
            ...prev,
            variables: {
                ...prev.variables,
                [key]: parseFloat(value) || 0
            }
        }));
    };
    
    return (
        <div className="scoring-range" style={{
            border: '1px solid #e0e0e0',
            borderRadius: '8px',
            marginBottom: '1rem',
            overflow: 'hidden'
        }}>
            {/* Range Header */}
            <div 
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1rem',
                    background: '#f8f9fa',
                    cursor: 'pointer'
                }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    {range.strategy_type === 'table' ? <Table size={18} /> : <FunctionSquare size={18} />}
                    <span style={{ fontWeight: 500 }}>
                        {range.name || `${range.min_players}-${range.max_players || '∞'} pelaajaa`}
                    </span>
                    <span style={{ 
                        fontSize: '0.8rem', 
                        padding: '0.2rem 0.5rem', 
                        borderRadius: '4px',
                        background: range.strategy_type === 'table' ? '#e3f2fd' : '#fff3e0',
                        color: range.strategy_type === 'table' ? '#1565c0' : '#e65100'
                    }}>
                        {range.strategy_type === 'table' ? 'Taulukko' : 'Kaava'}
                    </span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }} onClick={e => e.stopPropagation()}>
                    <button 
                        className="btn-icon"
                        onClick={() => setEditMode(!editMode)}
                        title="Muokkaa"
                    >
                        <Edit2 size={16} />
                    </button>
                    <button 
                        className="btn-icon danger"
                        onClick={() => onDelete(range.id)}
                        title="Poista"
                    >
                        <Trash2 size={16} />
                    </button>
                </div>
            </div>
            
            {/* Expanded Content */}
            {isExpanded && (
                <div style={{ padding: '1rem', borderTop: '1px solid #e0e0e0' }}>
                    {range.strategy_type === 'table' ? (
                        /* Table Editor */
                        <div>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                    <thead>
                                        <tr style={{ background: '#f5f0e6' }}>
                                            <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Pelaajat</th>
                                            {Array.from({ length: 8 }, (_, i) => i + 1).map(rank => (
                                                <th key={rank} style={{ padding: '0.5rem', border: '1px solid #ddd' }}>
                                                    {rank}. sija
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Array.from({ length: (range.max_players || 8) - range.min_players + 1 }, (_, i) => range.min_players + i)
                                            .map(playerCount => (
                                                <tr key={playerCount}>
                                                    <td style={{ padding: '0.5rem', border: '1px solid #ddd', fontWeight: 'bold' }}>
                                                        {playerCount} pelaajaa
                                                    </td>
                                                    {Array.from({ length: 8 }, (_, i) => i + 1).map(rank => (
                                                        <td key={rank} style={{ padding: '0.25rem', border: '1px solid #ddd', textAlign: 'center' }}>
                                                            {rank <= playerCount ? (
                                                                editMode ? (
                                                                    <input
                                                                        type="number"
                                                                        min="0"
                                                                        value={tableRules[playerCount]?.[rank] ?? 0}
                                                                        onChange={e => updateTableValue(playerCount, rank, e.target.value)}
                                                                        style={{ 
                                                                            width: '50px', 
                                                                            textAlign: 'center',
                                                                            padding: '0.25rem'
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <span>{tableRules[playerCount]?.[rank] ?? 0}</span>
                                                                )
                                                            ) : (
                                                                <span style={{ color: '#ccc' }}>-</span>
                                                            )}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>
                            
                            {editMode && (
                                <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                                    <button 
                                        className="btn-primary"
                                        onClick={saveTableRules}
                                        disabled={saving}
                                    >
                                        {saving ? <RefreshCw size={16} className="spinning" /> : <Save size={16} />}
                                        {' '}Tallenna
                                    </button>
                                    <button 
                                        className="btn-secondary"
                                        onClick={() => setEditMode(false)}
                                    >
                                        Peruuta
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* Formula Editor */
                        <div>
                            {/* Formula Templates */}
                            {editMode && (
                                <div style={{ marginBottom: '1rem' }}>
                                    <label style={{ fontWeight: 500, marginBottom: '0.5rem', display: 'block' }}>
                                        Mallipohjat:
                                    </label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                        <button
                                            type="button"
                                            className="btn-secondary"
                                            style={{ fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}
                                            onClick={() => setFormula({
                                                expression: 'rank == 1 ? floor(pot * rank1Percent) : rank == 2 ? floor(pot * rank2Percent) : rank == 3 ? floor(pot * rank3Percent) : 0',
                                                variables: { multiplier: 2, rank1Percent: 0.5, rank2Percent: 0.3, rank3Percent: 0.2 }
                                            })}
                                        >
                                            Top 3 saa pisteet
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-secondary"
                                            style={{ fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}
                                            onClick={() => setFormula({
                                                expression: 'playerCount - rank + 1',
                                                variables: {}
                                            })}
                                        >
                                            Lineaarinen (N pistettä → 1)
                                        </button>
                                        <button
                                            type="button"
                                            className="btn-secondary"
                                            style={{ fontSize: '0.85rem', padding: '0.5rem 0.75rem' }}
                                            onClick={() => setFormula({
                                                expression: 'rank == 1 ? pot : 0',
                                                variables: { multiplier: 2 }
                                            })}
                                        >
                                            Voittaja vie kaiken
                                        </button>
                                    </div>
                                </div>
                            )}
                            
                            <div className="form-group">
                                <label>Kaava (yksittäinen lauseke)</label>
                                <textarea
                                    value={formula.expression}
                                    onChange={e => setFormula(prev => ({ ...prev, expression: e.target.value }))}
                                    disabled={!editMode}
                                    rows={3}
                                    style={{ 
                                        fontFamily: 'monospace',
                                        width: '100%',
                                        padding: '0.75rem',
                                        background: editMode ? '#fff' : '#f5f5f5'
                                    }}
                                    placeholder="rank == 1 ? floor(pot * rank1Percent) : rank == 2 ? floor(pot * rank2Percent) : 0"
                                />
                                <div style={{ 
                                    marginTop: '0.5rem', 
                                    padding: '0.75rem', 
                                    background: '#f0f8ff', 
                                    borderRadius: '4px',
                                    fontSize: '0.85rem',
                                    color: '#333'
                                }}>
                                    <strong>Syntaksi:</strong> Kaavan tulee olla yksittäinen lauseke (ei sijoituksia <code>=</code>).<br/>
                                    <strong>Ehto:</strong> <code>ehto ? arvo_tosi : arvo_epätosi</code><br/>
                                    <strong>Funktiot:</strong> <code>floor()</code>, <code>ceil()</code>, <code>round()</code>, <code>max()</code>, <code>min()</code><br/>
                                    <strong>Muuttujat:</strong> <code>playerCount</code>, <code>rank</code>, <code>pot</code> (= playerCount × multiplier)
                                </div>
                                
                                {/* Formula Error Display */}
                                {formulaError && (
                                    <div style={{ 
                                        marginTop: '0.75rem', 
                                        padding: '0.75rem', 
                                        background: '#ffebee', 
                                        border: '1px solid #f44336',
                                        borderRadius: '4px',
                                        fontSize: '0.85rem'
                                    }}>
                                        <div style={{ color: '#c62828', fontWeight: 500, marginBottom: '0.5rem' }}>
                                            ⚠️ {formulaError.message}
                                        </div>
                                        {formulaError.suggestion && (
                                            <div style={{ 
                                                color: '#333', 
                                                whiteSpace: 'pre-wrap',
                                                background: '#fff',
                                                padding: '0.5rem',
                                                borderRadius: '4px',
                                                fontFamily: 'monospace',
                                                fontSize: '0.8rem'
                                            }}>
                                                💡 {formulaError.suggestion}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            
                            <div style={{ marginTop: '1rem' }}>
                                <label style={{ fontWeight: 500 }}>Muuttujat:</label>
                                <div style={{ 
                                    display: 'grid', 
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                                    gap: '1rem',
                                    marginTop: '0.5rem'
                                }}>
                                    <div className="form-group">
                                        <label>multiplier</label>
                                        <input
                                            type="number"
                                            step="0.1"
                                            value={formula.variables?.multiplier ?? 2}
                                            onChange={e => updateFormulaVariable('multiplier', e.target.value)}
                                            disabled={!editMode}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>rank1Percent</label>
                                        <input
                                            type="number"
                                            step="0.05"
                                            min="0"
                                            max="1"
                                            value={formula.variables?.rank1Percent ?? 0.5}
                                            onChange={e => updateFormulaVariable('rank1Percent', e.target.value)}
                                            disabled={!editMode}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>rank2Percent</label>
                                        <input
                                            type="number"
                                            step="0.05"
                                            min="0"
                                            max="1"
                                            value={formula.variables?.rank2Percent ?? 0.3}
                                            onChange={e => updateFormulaVariable('rank2Percent', e.target.value)}
                                            disabled={!editMode}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>rank3Percent</label>
                                        <input
                                            type="number"
                                            step="0.05"
                                            min="0"
                                            max="1"
                                            value={formula.variables?.rank3Percent ?? 0.2}
                                            onChange={e => updateFormulaVariable('rank3Percent', e.target.value)}
                                            disabled={!editMode}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            {editMode && (
                                <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                                    <button 
                                        className="btn-primary"
                                        onClick={saveFormula}
                                        disabled={saving}
                                    >
                                        {saving ? <RefreshCw size={16} className="spinning" /> : <Save size={16} />}
                                        {' '}Tallenna
                                    </button>
                                    <button 
                                        className="btn-secondary"
                                        onClick={() => setEditMode(false)}
                                    >
                                        Peruuta
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Add Range Modal
const AddRangeModal = ({ strategyId, onClose, onCreated, apiPrefix = '/api/superadmin/scoring' }) => {
    const [rangeType, setRangeType] = useState('table');
    const [minPlayers, setMinPlayers] = useState(2);
    const [maxPlayers, setMaxPlayers] = useState(8);
    const [noMaxLimit, setNoMaxLimit] = useState(false);
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    
    const handleCreate = async () => {
        setSaving(true);
        try {
            const endpoint = apiPrefix.includes('superadmin')
                ? `/api/superadmin/scoring/strategies/${strategyId}/ranges`
                : `/api/scoring/strategies/${strategyId}/ranges`;
            const res = await apiFetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: name || `${minPlayers}-${noMaxLimit ? '∞' : maxPlayers} pelaajaa`,
                    min_players: minPlayers,
                    max_players: noMaxLimit ? null : maxPlayers,
                    strategy_type: rangeType
                })
            });
            
            if (res.ok) {
                onCreated();
            }
        } catch (err) {
            console.error('Failed to create range:', err);
        } finally {
            setSaving(false);
        }
    };
    
    return (
        <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
        }} onClick={onClose}>
            <div style={{
                background: 'white',
                borderRadius: '12px',
                maxWidth: '500px',
                width: '90%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
            }} onClick={e => e.stopPropagation()}>
                <div style={{
                    padding: '1rem 1.5rem',
                    borderBottom: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
                    <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Plus size={20} /> Lisää pelaajamääräväli
                    </h3>
                    <button 
                        onClick={onClose}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0.25rem'
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>
                
                <div style={{ padding: '1.5rem' }}>
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Nimi (valinnainen)</label>
                        <input
                            type="text"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            placeholder="esim. Pienet pelit"
                            style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                        />
                    </div>
                    
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Minimi pelaajamäärä</label>
                            <input
                                type="number"
                                min="1"
                                value={minPlayers}
                                onChange={e => setMinPlayers(parseInt(e.target.value) || 1)}
                                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Maksimi pelaajamäärä</label>
                            <input
                                type="number"
                                min={minPlayers}
                                value={maxPlayers}
                                onChange={e => setMaxPlayers(parseInt(e.target.value) || minPlayers)}
                                disabled={noMaxLimit}
                                style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #ccc' }}
                            />
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem' }}>
                                <input
                                    type="checkbox"
                                    checked={noMaxLimit}
                                    onChange={e => setNoMaxLimit(e.target.checked)}
                                />
                                Ei ylärajaa (∞)
                            </label>
                        </div>
                    </div>
                    
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>Strategiatyyppi</label>
                        <div style={{ display: 'flex', gap: '1rem' }}>
                            <label style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0.5rem',
                                padding: '0.75rem 1rem',
                                border: rangeType === 'table' ? '2px solid #d35400' : '1px solid #ddd',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                background: rangeType === 'table' ? '#fff8f0' : '#fff'
                            }}>
                                <input
                                    type="radio"
                                    checked={rangeType === 'table'}
                                    onChange={() => setRangeType('table')}
                                />
                                <Table size={18} />
                                Taulukko
                            </label>
                            <label style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '0.5rem',
                                padding: '0.75rem 1rem',
                                border: rangeType === 'formula' ? '2px solid #d35400' : '1px solid #ddd',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                background: rangeType === 'formula' ? '#fff8f0' : '#fff'
                            }}>
                                <input
                                    type="radio"
                                    checked={rangeType === 'formula'}
                                    onChange={() => setRangeType('formula')}
                                />
                                <FunctionSquare size={18} />
                                Kaava
                            </label>
                        </div>
                    </div>
                </div>
                
                <div style={{
                    padding: '1rem 1.5rem',
                    borderTop: '1px solid #e0e0e0',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '0.5rem'
                }}>
                    <button 
                        onClick={onClose}
                        style={{
                            padding: '0.5rem 1rem',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            background: 'white',
                            cursor: 'pointer'
                        }}
                    >
                        Peruuta
                    </button>
                    <button 
                        onClick={handleCreate}
                        disabled={saving}
                        style={{
                            padding: '0.5rem 1rem',
                            border: 'none',
                            borderRadius: '4px',
                            background: '#d35400',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                        }}
                    >
                        {saving ? <RefreshCw size={16} className="spinning" /> : <Plus size={16} />}
                        {' '}Luo väli
                    </button>
                </div>
            </div>
        </div>
    );
};

// Main Component
const ScoringStrategyManager = ({ 
    clubId = null, 
    title = "Pisteytysstrategiat",
    apiPrefix = '/api/superadmin/scoring',
    readOnly = false,
    showTemplatesOnly = true,
    initialStrategyId = null  // If provided, auto-select this strategy
}) => {
    const [strategies, setStrategies] = useState([]);
    const [selectedStrategy, setSelectedStrategy] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showAddRange, setShowAddRange] = useState(false);
    const [preview, setPreview] = useState(null);
    const [testPlayers, setTestPlayers] = useState(4);
    const [testRank, setTestRank] = useState(1);
    const [editingStrategyName, setEditingStrategyName] = useState(false);
    const [strategyNameInput, setStrategyNameInput] = useState('');
    const [strategyDescInput, setStrategyDescInput] = useState('');
    
    useEffect(() => {
        fetchStrategies();
    }, []);
    
    const fetchStrategies = async () => {
        setLoading(true);
        try {
            const endpoint = apiPrefix.includes('superadmin')
                ? `${apiPrefix}/strategies`
                : '/api/scoring/strategies';
            const res = await apiFetch(endpoint);
            console.log('[ScoringManager] Fetch strategies response:', res.status);
            if (res.ok) {
                let data = await res.json();
                console.log('[ScoringManager] Strategies loaded:', data);
                
                // For club admin, filter out templates and platform defaults - show only owned strategies
                if (!apiPrefix.includes('superadmin')) {
                    data = data.filter(s => s.is_owned === 1 || s.is_owned === true);
                }
                
                setStrategies(data);
                
                // If initialStrategyId provided, select that
                if (initialStrategyId) {
                    await loadStrategy(initialStrategyId);
                } else {
                    // Auto-select platform default or first strategy
                    const platformDefault = data.find(s => s.club_id === null);
                    if (platformDefault) {
                        await loadStrategy(platformDefault.id);
                    } else if (data.length > 0) {
                        await loadStrategy(data[0].id);
                    }
                }
            } else {
                const errorText = await res.text();
                console.error('[ScoringManager] Fetch strategies failed:', res.status, errorText);
            }
        } catch (err) {
            console.error('Failed to fetch strategies:', err);
        } finally {
            setLoading(false);
        }
    };
    
    const loadStrategy = async (strategyId) => {
        try {
            const endpoint = apiPrefix.includes('superadmin')
                ? `${apiPrefix}/strategies/${strategyId}`
                : `/api/scoring/strategies/${strategyId}`;
            const res = await apiFetch(endpoint);
            if (res.ok) {
                const data = await res.json();
                setSelectedStrategy(data);
            }
        } catch (err) {
            console.error('Failed to load strategy:', err);
        }
    };
    
    const deleteRange = async (rangeId) => {
        if (readOnly) return;
        if (!window.confirm('Haluatko varmasti poistaa tämän välin?')) return;
        
        try {
            const endpoint = apiPrefix.includes('superadmin')
                ? `/api/superadmin/scoring/ranges/${rangeId}`
                : `/api/scoring/ranges/${rangeId}`;
            const res = await apiFetch(endpoint, {
                method: 'DELETE'
            });
            if (res.ok) {
                loadStrategy(selectedStrategy.id);
            }
        } catch (err) {
            console.error('Failed to delete range:', err);
        }
    };
    
    const startEditingStrategy = () => {
        setStrategyNameInput(selectedStrategy?.name || '');
        setStrategyDescInput(selectedStrategy?.description || '');
        setEditingStrategyName(true);
    };
    
    const saveStrategyName = async () => {
        if (readOnly) return;
        if (!selectedStrategy || !strategyNameInput.trim()) return;
        
        try {
            const endpoint = apiPrefix.includes('superadmin')
                ? `/api/superadmin/scoring/strategies/${selectedStrategy.id}`
                : `/api/scoring/strategies/${selectedStrategy.id}`;
            const res = await apiFetch(endpoint, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: strategyNameInput.trim(),
                    description: strategyDescInput.trim()
                })
            });
            
            if (res.ok) {
                // Update local state
                setSelectedStrategy(prev => ({
                    ...prev,
                    name: strategyNameInput.trim(),
                    description: strategyDescInput.trim()
                }));
                // Update strategies list
                setStrategies(prev => prev.map(s => 
                    s.id === selectedStrategy.id 
                        ? { ...s, name: strategyNameInput.trim(), description: strategyDescInput.trim() }
                        : s
                ));
                setEditingStrategyName(false);
            } else {
                console.error('Failed to save strategy name');
            }
        } catch (err) {
            console.error('Failed to save strategy name:', err);
        }
    };
    
    const cancelEditingStrategy = () => {
        setEditingStrategyName(false);
        setStrategyNameInput('');
        setStrategyDescInput('');
    };
    
    // Calculate preview points
    const previewPoints = useMemo(() => {
        if (!selectedStrategy) return 0;
        return calculatePreview(selectedStrategy, testPlayers, testRank);
    }, [selectedStrategy, testPlayers, testRank]);
    
    if (loading) {
        return (
            <div className="loading-container" style={{ padding: '2rem', textAlign: 'center' }}>
                <RefreshCw size={32} className="spinning" />
                <p>Ladataan pisteytysstrategioita...</p>
            </div>
        );
    }
    
    return (
        <div className="scoring-strategy-manager">
            <div className="card">
                <h2><Layers size={24} /> {title}</h2>
                
                {/* Strategy Selector */}
                <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                    <label>Valitse strategia</label>
                    <select
                        value={selectedStrategy?.id || ''}
                        onChange={e => loadStrategy(parseInt(e.target.value))}
                        style={{ maxWidth: '400px' }}
                    >
                        {strategies.map(s => (
                            <option key={s.id} value={s.id}>
                                {s.club_id === null ? '🌐 ' : '🏠 '}
                                {s.name}
                                {s.club_name ? ` (${s.club_name})` : ' (Alustan oletus)'}
                            </option>
                        ))}
                    </select>
                </div>
                
                {selectedStrategy && (
                    <>
                        {/* Strategy Info */}
                        <div style={{ 
                            background: '#f8f9fa', 
                            padding: '1rem', 
                            borderRadius: '8px',
                            marginBottom: '1.5rem'
                        }}>
                            {editingStrategyName ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                                            Strategian nimi
                                        </label>
                                        <input
                                            type="text"
                                            value={strategyNameInput}
                                            onChange={e => setStrategyNameInput(e.target.value)}
                                            placeholder="Esim. Masan Peliklubi Default"
                                            style={{ width: '100%', maxWidth: '400px' }}
                                            autoFocus
                                        />
                                    </div>
                                    <div>
                                        <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: 500 }}>
                                            Kuvaus (valinnainen)
                                        </label>
                                        <input
                                            type="text"
                                            value={strategyDescInput}
                                            onChange={e => setStrategyDescInput(e.target.value)}
                                            placeholder="Esim. 2-8 pelaajaa taulukko, 9+ kaava"
                                            style={{ width: '100%', maxWidth: '400px' }}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                        <button 
                                            className="btn-primary"
                                            onClick={saveStrategyName}
                                            disabled={!strategyNameInput.trim()}
                                        >
                                            <Check size={16} /> Tallenna
                                        </button>
                                        <button 
                                            className="btn-secondary"
                                            onClick={cancelEditingStrategy}
                                        >
                                            <X size={16} /> Peruuta
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <h3 style={{ margin: 0 }}>{selectedStrategy.name}</h3>
                                            <button 
                                                onClick={startEditingStrategy}
                                                style={{ 
                                                    background: 'none', 
                                                    border: 'none', 
                                                    cursor: 'pointer',
                                                    padding: '0.25rem',
                                                    color: '#666',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                                title="Muokkaa nimeä"
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                        </div>
                                        {selectedStrategy.description && (
                                            <p style={{ color: '#666', margin: '0.5rem 0 0' }}>{selectedStrategy.description}</p>
                                        )}
                                    </div>
                                    {selectedStrategy.club_id === null && (
                                        <span style={{
                                            background: '#d35400',
                                            color: 'white',
                                            padding: '0.25rem 0.75rem',
                                            borderRadius: '4px',
                                            fontSize: '0.8rem'
                                        }}>
                                            Platform Default
                                        </span>
                                    )}
                                </div>
                            )}
                        </div>
                        
                        {/* Ranges */}
                        <div style={{ marginBottom: '1.5rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <h3 style={{ margin: 0 }}>Pelaajamäärävälit</h3>
                                <button 
                                    className="btn-secondary"
                                    onClick={() => setShowAddRange(true)}
                                >
                                    <Plus size={16} /> Lisää väli
                                </button>
                            </div>
                            
                            {selectedStrategy.ranges?.length === 0 ? (
                                <div style={{ 
                                    padding: '2rem', 
                                    textAlign: 'center', 
                                    background: '#f8f9fa',
                                    borderRadius: '8px',
                                    color: '#666'
                                }}>
                                    <Info size={32} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
                                    <p>Ei pelaajamäärävälejä. Lisää ensimmäinen väli aloittaaksesi.</p>
                                </div>
                            ) : (
                                selectedStrategy.ranges?.map(range => (
                                    <RangeEditor
                                        key={range.id}
                                        range={range}
                                        strategyId={selectedStrategy.id}
                                        onUpdate={() => loadStrategy(selectedStrategy.id)}
                                        onDelete={deleteRange}
                                        apiPrefix={apiPrefix}
                                        readOnly={readOnly || selectedStrategy.is_template || selectedStrategy.is_readonly}
                                    />
                                ))
                            )}
                        </div>
                    </>
                )}
            </div>
            
            {/* Preview Calculator */}
            {selectedStrategy && (
                <div className="card" style={{ marginTop: '1.5rem', background: '#f0f8ff' }}>
                    <h3><Calculator size={20} /> Laskin (Esikatselu)</h3>
                    <p style={{ color: '#666', marginBottom: '1rem' }}>
                        Testaa miten nykyiset asetukset vaikuttavat pisteisiin.
                    </p>
                    
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Pelaajamäärä</label>
                            <input 
                                type="number" 
                                min="2" 
                                max="20"
                                value={testPlayers}
                                onChange={e => setTestPlayers(parseInt(e.target.value) || 2)}
                                style={{ width: '100px' }}
                            />
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label>Sijoitus</label>
                            <input 
                                type="number" 
                                min="1" 
                                max={testPlayers}
                                value={testRank}
                                onChange={e => setTestRank(parseInt(e.target.value) || 1)}
                                style={{ width: '100px' }}
                            />
                        </div>
                        <div style={{ 
                            background: '#d35400', 
                            color: 'white', 
                            padding: '1rem 2rem', 
                            borderRadius: '8px',
                            textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '0.8rem', opacity: 0.8 }}>Pisteet</div>
                            <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{previewPoints}</div>
                        </div>
                    </div>
                    
                    {/* Full Preview Table */}
                    <div style={{ marginTop: '1.5rem' }}>
                        <h4>Täysi pistetaulukko (esikatselu)</h4>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                <thead>
                                    <tr style={{ background: '#e3f2fd' }}>
                                        <th style={{ padding: '0.5rem', border: '1px solid #ddd' }}>Pelaajat</th>
                                        {[1, 2, 3, 4, 5, 6, 7, 8].map(rank => (
                                            <th key={rank} style={{ padding: '0.5rem', border: '1px solid #ddd' }}>
                                                {rank}. sija
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {[2, 3, 4, 5, 6, 7, 8, 9, 10, 12].map(playerCount => (
                                        <tr key={playerCount}>
                                            <td style={{ padding: '0.5rem', border: '1px solid #ddd', fontWeight: 'bold' }}>
                                                {playerCount}
                                            </td>
                                            {[1, 2, 3, 4, 5, 6, 7, 8].map(rank => (
                                                <td key={rank} style={{ 
                                                    padding: '0.5rem', 
                                                    border: '1px solid #ddd',
                                                    textAlign: 'center',
                                                    background: rank <= playerCount ? '#fff' : '#f5f5f5'
                                                }}>
                                                    {rank <= playerCount 
                                                        ? calculatePreview(selectedStrategy, playerCount, rank) 
                                                        : '-'}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Add Range Modal */}
            {showAddRange && selectedStrategy && !readOnly && !selectedStrategy.is_template && !selectedStrategy.is_readonly && (
                <AddRangeModal
                    strategyId={selectedStrategy.id}
                    onClose={() => setShowAddRange(false)}
                    onCreated={() => {
                        setShowAddRange(false);
                        loadStrategy(selectedStrategy.id);
                    }}
                    apiPrefix={apiPrefix}
                />
            )}
        </div>
    );
};

export default ScoringStrategyManager;

